import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  safetyStockService,
  type SafetyStockEngineConfig,
} from '@/services/safetyStockService';

const STALE_MS = 60 * 1000;
const GC_MS = 10 * 60 * 1000;

export const safetyStockConfigQueryKey = ['safety-stock-config'] as const;
export const safetyStockScopeQueryKey = (
  activeOnly: boolean,
  warehouseId: number | null,
) => ['safety-stock-scope', activeOnly, warehouseId] as const;
export const safetyStockHealthQueryKey = ['safety-stock-health'] as const;
export const safetyStockRunsQueryKey = ['safety-stock-runs'] as const;

export function useSafetyStockConfig() {
  return useQuery({
    queryKey: safetyStockConfigQueryKey,
    queryFn: ({ signal }) => safetyStockService.getConfig({ signal }),
    staleTime: STALE_MS,
    gcTime: GC_MS,
  });
}

export function useSafetyStockHealth() {
  return useQuery({
    queryKey: safetyStockHealthQueryKey,
    queryFn: ({ signal }) => safetyStockService.getHealth({ signal }),
    staleTime: 30 * 1000,
    gcTime: GC_MS,
    retry: 1,
  });
}

export function useSafetyStockScope(
  activeProductsOnly: boolean,
  warehouseId: number | null,
  enabled = true,
) {
  return useQuery({
    queryKey: safetyStockScopeQueryKey(activeProductsOnly, warehouseId),
    queryFn: ({ signal }) =>
      safetyStockService.previewScope(
        {
          active_products_only: activeProductsOnly,
          warehouse_id: warehouseId as number,
        },
        { signal },
      ),
    enabled: enabled && typeof warehouseId === 'number',
    staleTime: STALE_MS,
    gcTime: GC_MS,
  });
}

export function useLatestAbcXyzBreakdown(
  warehouseId: number | null,
  enabled = true,
) {
  return useQuery({
    queryKey: [...safetyStockRunsQueryKey, 'latest-abc-xyz', warehouseId],
    enabled: enabled && typeof warehouseId === 'number',
    staleTime: STALE_MS,
    gcTime: GC_MS,
    queryFn: async ({ signal }) => {
      const list = await safetyStockService.listRuns(
        { page: 1, page_size: 10, warehouse_id: warehouseId as number },
        { signal },
      );
      const candidate = list.results.find(
        (r) =>
          r.summary_ready &&
          (r.status === 'SUCCESS' || r.status === 'PARTIAL_SUCCESS'),
      );
      if (!candidate) return null;
      return safetyStockService.getRunBreakdown(
        candidate.run_id,
        'ABC_XYZ',
        { warehouse_id: warehouseId as number },
        { signal },
      );
    },
  });
}

export function useSaveSafetyStockConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<SafetyStockEngineConfig>) =>
      safetyStockService.updateConfig(payload),
    onSuccess: (data) => {
      queryClient.setQueryData(safetyStockConfigQueryKey, data);
      void queryClient.invalidateQueries({ queryKey: ['safety-stock-scope'] });
    },
  });
}
